import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { T } from 'components/Translation/Translation';
import { fieldMap } from 'utils/fieldMap';
import errorMessage from 'utils/errorMessage';
import scrollTo from 'utils/scrollTo';
import content from 'utils/content';
import getElementOffset from 'utils/getElementOffset';
import { updateField, updateFieldNoPersist } from 'app/actions/formActions';
import { useFormValidator } from 'utils/useFormValidator';
import { useAppDispatch, useAppSelector } from 'app/hooks';

const PROP_BLACKLIST = [
	'section',
	'stage',
	'fieldName',
	'formActions',
	'dataAttrs',
	'extraValue',
];

const Container = styled.div`
	position: relative;
	width: 100%;
	${(props: any) => (props.type === 'checkbox' ? 'padding-top: 4rem' : '')}
`;

const Helper = styled.span`
	display: block;

	@media ${(props) => props.theme.md} {
		${(props: any) => (!props.static ? 'position: relative;' : '')}
		${(props: any) => (!props.static ? 'top: -1.8rem;' : '')};
	}

	span {
		font-size: 1.3rem;
		color: ${(props) => props.theme.dove};
	}
`;

export const Field = (props) => {
	const {
		section,
		stage,
		name,
		type,
		customValueStore,
		label,
		extraValue,
		helper,
		translation,
		required,
		onChange,
		noValidate,
		validator,
		hint,
		dataAttrs,
	} = props;
	const fieldName = `${section}_${stage}_${name}`;
	const [error, setError] = useState(null);
	const domNode = useRef(null);
	const dispatch = useAppDispatch();
	const fieldValue = useAppSelector((state) => {
		if (customValueStore) {
			return customValueStore;
		}

		if (type === 'monthyear') {
			return {
				month: state.values[`${fieldName}_month`] || '',
				year: state.values[`${fieldName}_year`] || '',
			};
		}

		const val =
			type === 'password'
				? state.nopersist[fieldName]
				: state.values[fieldName];

		return val || '';
	});
	const upload = useAppSelector((state) => {
		if (type !== 'file') return null;
		return state.uploads.files[fieldName];
	});
	const { registerField, unregisterField } = useFormValidator();

	const validate = (forceMessage) => {
		const val = type === 'file' ? upload : fieldValue;
		const extraVal = type === 'file' || extraValue ? extraValue : required;

		const errorMessages =
			forceMessage ||
			errorMessage(
				{ type, required, label, noValidate, validator, hint },
				val,
				extraVal,
			);

		if (errorMessages) {
			setError(errorMessages);

			return false;
		} else {
			if (error) {
				setError(null);
			}
		}

		return true;
	};

	const scrollToAndFocus = () => {
		if (!domNode.current) return;

		// @ts-expect-error TS(2339): Property 'querySelectorAll' does not exist on type... Remove this comment to see the full error message
		let fields = domNode.current.querySelectorAll('input');

		scrollTo(getElementOffset(domNode.current).top - 100, null, 500);

		if (!fields.length) {
			// @ts-expect-error TS(2339): Property 'querySelectorAll' does not exist on type... Remove this comment to see the full error message
			fields = domNode.current.querySelectorAll('select');

			if (!fields.length) {
				// @ts-expect-error TS(2339): Property 'querySelectorAll' does not exist on type... Remove this comment to see the full error message
				fields = domNode.current.querySelectorAll('textarea');
			}
		}

		if (fields.length) {
			fields[0].focus();
		}
	};

	// @ts-expect-error TS(2345): Argument of type '() => () => boolean' is not assi... Remove this comment to see the full error message
	useEffect(() => {
		registerField(fieldName, validate, scrollToAndFocus);

		return () => unregisterField(fieldName);
	});

	const shouldShowHelper = () => {
		return (
			(translation || helper) &&
			!error &&
			type !== 'separator' &&
			type !== 'heading' &&
			type !== 'file' &&
			type !== 'color' &&
			type !== 'radiogroup'
		);
	};

	const renderHelper = () => {
		if (!shouldShowHelper()) {
			return null;
		}

		if (helper) {
			return (
				// @ts-expect-error TS(2769): No overload matches this call.
				<Helper static={type === 'checkbox'}>
					<span>{helper}</span>
				</Helper>
			);
		}

		const translationKey = translation || `forms/${name}`;

		return (
			// @ts-expect-error TS(2769): No overload matches this call.
			<Helper static={type === 'checkbox'}>
				<T name={translationKey} />
			</Helper>
		);
	};

	const renderHelperWithError = () => {
		if (type === 'radiogroup' || type === 'checkboxset') {
			return null;
		}

		return (
			// @ts-expect-error TS(2769): No overload matches this call.
			<Helper static>
				<T name={`forms.${name}`} />
			</Helper>
		);
	};

	const handleChange = (newValue, name) => {
		const usedName = typeof name === 'string' ? name : fieldName;

		if (typeof onChange === 'function') {
			onChange(newValue, name);
		} else {
			if (type === 'password') {
				dispatch(updateFieldNoPersist(usedName, newValue));
			} else {
				dispatch(updateField(usedName, newValue));
			}
		}
	};

	const handleBlur = () => {
		if (error) {
			validate(false);
		}
	};

	const getAllowedProps = () => {
		const fieldProps = {
			...props,
			// @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
			label: content(translation, null, label),
		};

		Object.keys(fieldProps).forEach((prop) => {
			if (PROP_BLACKLIST.indexOf(prop) !== -1) {
				delete fieldProps[prop];
			}
		});

		if (error) {
			fieldProps.error = (
				<span>
					{error} {renderHelperWithError()}
				</span>
			);
		}

		// TODO: not needed once Typescript is properly typing component props
		if (fieldProps.disabled !== undefined) {
			fieldProps.disabled = Boolean(fieldProps.disabled);
		}

		return fieldProps;
	};

	const FormField = fieldMap[type];
	const extraProps = {};

	if (dataAttrs) {
		Object.keys(dataAttrs).forEach((attr) => {
			if (Object.prototype.hasOwnProperty.call(dataAttrs, attr)) {
				extraProps[`data-${attr}`] = dataAttrs[attr];
			}
		});
	}

	switch (type) {
		case 'switch':
		case 'checkbox':
			// @ts-expect-error TS(2339): Property 'checked' does not exist on type '{}'.
			extraProps.checked = Boolean(fieldValue);
			break;
		case 'file':
			// @ts-expect-error TS(2339): Property 'allowMultipleFiles' does not exist on ty... Remove this comment to see the full error message
			extraProps.allowMultipleFiles = true;
			break;
	}

	if (!FormField) {
		console.warn('Field type does not exist', type);
		return null;
	}

	return (
		// @ts-expect-error TS(2769): No overload matches this call.
		<Container type={type} ref={domNode}>
			<FormField
				{...getAllowedProps()}
				{...extraProps}
				name={fieldName}
				value={fieldValue}
				onChange={handleChange}
				onBlur={handleBlur}
				// forces Chrome to stop caching form data (Chrome doesn't comply with the spec for autocomplete="off")
				autoComplete="user-password"
			/>
			{renderHelper()}
		</Container>
	);
};
